import { gatewayApi as api } from './gatewayApi';

// Interfaces
export interface EventType {
  Id?: number;
  Name: string;
  Description?: string;
  Active: boolean;
  CreatedDate?: string;
  ModifiedDate?: string;
}

export interface EventTypeListResponse {
  data: EventType[];
  total: number;
}

export interface EventTypeSearchParams {
  page?: number;
  pageSize?: number;
  search?: string;
  active?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// API Service
export const eventTypeApi = {
  // CRUD Operations
  getAll: (params?: EventTypeSearchParams): Promise<EventTypeListResponse> =>
    api.get('/eventtype', params),

  getById: (id: number): Promise<EventType> =>
    api.get(`/eventtype/${id}`),

  create: (eventType: Omit<EventType, 'Id'>): Promise<EventType> =>
    api.post('/eventtype', eventType),

  update: (id: number, eventType: Partial<EventType>): Promise<EventType> =>
    api.put(`/eventtype/${id}`, eventType),

  delete: (id: number): Promise<{ success: boolean; message: string }> =>
    api.delete(`/eventtype/${id}`),

  // Search and Filter
  search: (params: EventTypeSearchParams): Promise<EventTypeListResponse> =>
    api.post('/eventtype/search', params),

  // Bulk Operations
  bulkActivate: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.put('/eventtype/bulk-activate', { ids }),

  bulkDeactivate: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.put('/eventtype/bulk-deactivate', { ids }),

  bulkDelete: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.post('/eventtype/bulk-delete', { ids }),

  // Statistics
  getStatistics: (): Promise<{
    total: number;
    active: number;
    inactive: number;
  }> =>
    api.get('/eventtype/statistics'),

  // Export
  exportToCSV: (params?: EventTypeSearchParams): Promise<Blob> =>
    api.getBlob('/eventtype/export', { params }),

  // Validation
  validation: {
    validateEventType: (eventType: Partial<EventType>): string[] => {
      const errors: string[] = [];

      // Name validation
      if (!eventType.Name || eventType.Name.trim().length === 0) {
        errors.push('Event Type Name is required');
      } else if (eventType.Name.trim().length < 2) {
        errors.push('Event Type Name must be at least 2 characters long');
      } else if (eventType.Name.trim().length > 100) {
        errors.push('Event Type Name must be less than 100 characters');
      }

      // Description validation (optional but if provided, validate length)
      if (eventType.Description && eventType.Description.trim().length > 500) {
        errors.push('Description must be less than 500 characters');
      }

      // Active validation
      if (eventType.Active === undefined || eventType.Active === null) {
        errors.push('Active status is required');
      }

      return errors;
    },

    validateBulkOperation: (ids: number[]): string[] => {
      const errors: string[] = [];

      if (!ids || ids.length === 0) {
        errors.push('At least one Event Type must be selected');
      }

      if (ids.length > 100) {
        errors.push('Cannot perform bulk operation on more than 100 items at once');
      }

      return errors;
    }
  }
};

export default eventTypeApi; 